import {isNil} from 'es-toolkit'

import {
  formatPrice as formatPriceWithCurrency,
  getCurrencySymbol,
} from '../../../commons/src/libs/currency'

import {Dimension, OptionType, Tolerance} from '../types'

import {FileUnit, ModelDimensions} from '../order-types'

export const formatScaleFactor = (scale: number) => `${Math.trunc(scale * 1000) / 1000}`

export const formatDimension = (dimension: Dimension) => dimension.join(' x ')

export const formatModelDimensions = (dimension: ModelDimensions, unit: FileUnit) =>
  `${Object.values(dimension)
    .map(d => Math.trunc(d * 1000) / 1000)
    .join(' x ')} ${unit}`

function appendPluralization(n: number, singularTerm: string, pluralTerm: string) {
  return Number(n) === 1 ? singularTerm : pluralTerm
}

export function formatTimeRange(
  from: number | string | undefined,
  to: number | string | undefined,
  suffix?: string,
  pluralSuffix?: string
): string {
  const rangeSuffix = suffix || 'business day'
  const rangePluralSuffix = pluralSuffix || 'business days'

  const nFrom = Number(from)

  if (to !== undefined) {
    const nTo = Number(to)
    const diff = nTo - nFrom

    if (diff === 0) return formatTimeRange(from, undefined, rangeSuffix, rangePluralSuffix)
    if (diff < 0) return formatTimeRange(to, from, rangeSuffix, rangePluralSuffix)
    return `${from}-${to} ${appendPluralization(nTo, rangeSuffix, rangePluralSuffix)}`
  }

  if (nFrom) return `${from} ${appendPluralization(nFrom, rangeSuffix, rangePluralSuffix)}`
  return '–'
}

export const formatCurrency = (currency: string) => {
  return getCurrencySymbol(currency)
}

export const formatPrice = (
  value: number | null | undefined,
  currency: string,
  fixedDecimals?: number
) => {
  if (value === null || value === undefined) {
    return '-'
  }

  return formatPriceWithCurrency(value, currency, fixedDecimals)
}

export const formatPercentage = (percentage: number) => `${Number((percentage * 100).toFixed(2))}%`

export const formatFactorToPercentage = (factor: number) => formatPercentage(factor - 1)

export const formatDate = (timestamp: string) => new Date(timestamp).toLocaleDateString()

export const formatDateTime = (timestamp: string) => {
  const date = new Date(timestamp)
  const localeDate = date.toLocaleDateString()
  const localeTime = date.toLocaleTimeString(undefined, {timeStyle: 'short'})
  if (localeDate === 'Invalid Date') return localeDate
  return `${localeDate}, ${localeTime}`
}

export const formatInfillQuoteType = (values: string[]) => {
  const infillRegex = /^infill_(.*)$/
  const match = values.find(value => infillRegex.test(value))

  if (!match) {
    return ''
  }

  return `${match.replace('infill_', '')}%`
}

export const formatInfillQuoteTypesSorted = (values: OptionType[]) => {
  const infillRegex = /^infill_(.*)$/
  const onlyInfills = values.filter(value => infillRegex.test(value))
  const sortedInfillNumbers = onlyInfills
    .map(v => Number(v.replace('infill_', '')))
    .sort((a, b) => a - b)
  return sortedInfillNumbers.map(v => `${v}%`).join(', ')
}

const toleranceLabelMap: Record<Tolerance, string> = {
  [Tolerance.TOLERANCE_004]: '±.004" (±0.10mm)',
  [Tolerance.TOLERANCE_003]: '±.003" (±0.08mm)',
  [Tolerance.TOLERANCE_002]: '±.002" (±0.05mm)',
  [Tolerance.TOLERANCE_001]: '±.001" (±0.03mm)',
  [Tolerance.TOLERANCE_TIGHTER_001]: 'tighter than ±.001" (±0.03mm)',
}

const toleranceShortLabelMap: Record<Tolerance, string> = {
  [Tolerance.TOLERANCE_004]: '±.004"',
  [Tolerance.TOLERANCE_003]: '±.003"',
  [Tolerance.TOLERANCE_002]: '±.002"',
  [Tolerance.TOLERANCE_001]: '±.001"',
  [Tolerance.TOLERANCE_TIGHTER_001]: '< ±.001"',
}

export const getToleranceLabel = (tolerance: Tolerance, useShort = false) => {
  return useShort ? toleranceShortLabelMap[tolerance] || '' : toleranceLabelMap[tolerance] || ''
}

export const formatToleranceQuoteType = (values: string[], useShort?: boolean) => {
  const toleranceRegex = /^tolerance_(.*)$/
  const match = values.find(value => toleranceRegex.test(value))

  if (!match) {
    return ''
  }

  return getToleranceLabel(match as Tolerance, useShort)
}

export function formatFileSize(bytes: number, decimals?: number): string {
  if (bytes === 0) {
    return 'n/a'
  }
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)

  if (i === 0) {
    return `${bytes} ${sizes[i]}`
  }

  return `${(bytes / 1024 ** i).toFixed(typeof decimals === 'number' ? decimals : 1)} ${sizes[i]}`
}

export function formatDeliveryTime(deliveryTime: string, suffix?: string, pluralSuffix?: string) {
  const rangeSuffix = suffix || 'business day'
  const rangePluralSuffix = pluralSuffix || 'business days'

  return `${deliveryTime} ${appendPluralization(
    deliveryTime.includes('-') ? 2 : parseInt(deliveryTime, 10),
    rangeSuffix,
    rangePluralSuffix
  )}`
}

export const formatOptionalRange = (from: number | string, to?: number | string | null) => {
  if (isNil(to) || from === to) {
    return `${from}`
  }

  return [from, to].join(' - ')
}

export const formatMillimeters = (millimeters: number) => `${Number(millimeters.toFixed(2))} mm`

export const formatDensity = (density: number) => `${Number(density.toFixed(2))} g/cm³`

export const formatCncAxisTier = (cncAxisTier: number) => `${cncAxisTier}-axis`
